/**
 * PortalSmartLinks — Auto-routing URLs per affiliate
 *
 * One URL detects visitor geo/device/intent and routes to the highest-paying offer.
 * e.g., intentdrive.ai/go/AFF-J4K9 → auto-detects state → routes to best vertical.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link2, Plus, Loader2, Copy, Check, Pencil, Save, ArrowLeft, Trash2, Eye } from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface SmartLink {
  id: string;
  slug: string;
  name: string;
  default_vertical: string;
  auto_detect_vertical: boolean;
  geo_routing: Record<string, string>;
  device_routing: Record<string, string>;
  brand_name: string | null;
  brand_color: string | null;
  clicks: number;
  conversions: number;
  revenue: number;
  is_active: boolean;
  created_at: string;
}

const VERTICALS = [
  'auto_insurance', 'home_insurance', 'health_insurance', 'life_insurance',
  'debt_relief', 'personal_loan', 'mortgage_refi', 'solar', 'medicare',
];

export default function PortalSmartLinks() {
  const { user } = useAuth();
  const [links, setLinks] = useState<SmartLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<SmartLink>>({});
  const [saving, setSaving] = useState(false);

  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setLinks((await supabaseGet('smart_links?order=created_at.desc')) || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const genSlug = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 6; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
    return s;
  };

  const create = async () => {
    if (!formName || !user?.id) return;
    setCreating(true);
    try {
      await supabasePost('smart_links', { user_id: user.id, slug: formSlug || genSlug(), name: formName, default_vertical: 'auto_insurance', is_active: true });
      setShowCreate(false); setFormName(''); setFormSlug(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const startEdit = (l: SmartLink) => { setEditingId(l.id); setEditForm({ ...l }); };
  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, clicks, conversions, revenue, created_at, ...updates } = editForm as any;
      await supabasePatch(`smart_links?id=eq.${editingId}`, updates);
      setLinks(prev => prev.map(l => l.id === editingId ? { ...l, ...updates } : l));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`https://intentdrive.ai/go/${slug}`);
    setCopied(slug); setTimeout(() => setCopied(null), 2000);
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  // Geo routing helper
  const addGeoRoute = (state: string, vertical: string) => {
    const geo = { ...(editForm.geo_routing || {}) };
    geo[state] = vertical;
    u('geo_routing', geo);
  };
  const removeGeoRoute = (state: string) => {
    const geo = { ...(editForm.geo_routing || {}) };
    delete geo[state];
    u('geo_routing', geo);
  };

  if (editingId) {
    return (
      <PortalLayout>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
          </div>
          <h1 className="text-xl font-bold text-white">{editForm.name}</h1>
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-emerald-400 font-mono">
            https://intentdrive.ai/go/{editForm.slug}
          </div>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Name</label><input value={editForm.name || ''} onChange={e => u('name', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Default Vertical</label><select value={editForm.default_vertical} onChange={e => u('default_vertical', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">{VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}</select></div>
                <div><label className="text-xs text-slate-400 block mb-1">Brand Name (optional)</label><input value={editForm.brand_name || ''} onChange={e => u('brand_name', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Brand Color</label><div className="flex gap-2"><input type="color" value={editForm.brand_color || '#10b981'} onChange={e => u('brand_color', e.target.value)} className="w-10 h-10 rounded border border-slate-600" /><input value={editForm.brand_color || '#10b981'} onChange={e => u('brand_color', e.target.value)} className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm font-mono" /></div></div>
              </div>

              {/* Geo Routing */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-white mb-2">Geo Routing</h4>
                <p className="text-[10px] text-slate-500 mb-3">Override default vertical based on visitor's US state. Empty = use default.</p>
                {Object.entries(editForm.geo_routing || {}).map(([state, vert]) => (
                  <div key={state} className="flex gap-2 mb-1.5">
                    <input value={state} readOnly className="w-16 bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs text-center" />
                    <span className="text-slate-600 self-center">→</span>
                    <select value={vert} onChange={e => addGeoRoute(state, e.target.value)} className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs">{VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}</select>
                    <button onClick={() => removeGeoRoute(state)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input id="newGeoState" placeholder="State (e.g. CA)" className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                  <button onClick={() => { const el = document.getElementById('newGeoState') as HTMLInputElement; if (el?.value) { addGeoRoute(el.value.toUpperCase(), 'solar'); el.value = ''; } }} className="px-2 py-1 rounded bg-slate-700 text-[10px] text-slate-300 hover:bg-slate-600">+ Add State</button>
                </div>
              </div>

              {/* Device Routing */}
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-white mb-2">Device Routing</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Mobile → Vertical</label>
                    <select value={(editForm.device_routing as any)?.mobile || ''} onChange={e => u('device_routing', { ...(editForm.device_routing || {}), mobile: e.target.value })} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Same as default</option>
                      {VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Desktop → Vertical</label>
                    <select value={(editForm.device_routing as any)?.desktop || ''} onChange={e => u('device_routing', { ...(editForm.device_routing || {}), desktop: e.target.value })} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="">Same as default</option>
                      {VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Link2 className="h-6 w-6 text-emerald-400" /> Smart Links</h1>
            <p className="text-sm text-slate-400 mt-1">One URL that auto-detects geo + device and routes to the highest-paying offer.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4 mr-1" /> New Link</Button>
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Name *</label><input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Facebook Traffic Link" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
                <div><label className="text-xs text-slate-400 block mb-1">Custom Slug (optional)</label><input value={formSlug} onChange={e => setFormSlug(e.target.value)} placeholder="Auto-generated if empty" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>
              </div>
              <Button onClick={create} disabled={creating || !formName} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create</Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : links.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Link2 className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No smart links yet.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {links.map(l => (
              <Card key={l.id} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{l.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${l.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{l.is_active ? 'Active' : 'Paused'}</span>
                      </div>
                      <div className="text-xs text-emerald-400 font-mono mt-0.5">intentdrive.ai/go/{l.slug}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 flex gap-3">
                        <span>{l.clicks} clicks</span>
                        <span>{l.conversions} conv</span>
                        <span>${Number(l.revenue || 0).toFixed(2)} rev</span>
                        {l.clicks > 0 && <span>{(l.conversions / l.clicks * 100).toFixed(1)}% CVR</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => startEdit(l)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30"><Pencil className="h-3 w-3" /> Edit</button>
                      <button onClick={() => copyUrl(l.slug)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">
                        {copied === l.slug ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />} URL
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
